import React, { useContext, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlobalContext } from '@/app/providers/globalState';
import { useAuth } from '@/features/auth/AuthContext';
import { Header, Button, SparkIcon } from '@spark';
import TaskBreadcrumb from '@/features/tasks/components/TaskBreadcrumb';
import { allSubtasksShareSameConfig } from '@/features/tasks/utils/promptPropagation';
import '@/features/tasks/styles/UsecaseHeader.css';

const UsecaseHeader = ({ onCreateProject, onAddTask, onOpenTaskPrompt }) => {
    const navigate = useNavigate();
    const { role } = useAuth();
    const { getCurrentUsecase, currentSubGroup } = useContext(GlobalContext);
    const currentUsecase = getCurrentUsecase();
    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(0);

    useEffect(() => {
        const header = headerRef.current;
        if (!header) return undefined;

        const updateHeaderHeight = () => {
            setHeaderHeight(header.offsetHeight);
        };

        updateHeaderHeight();
        window.addEventListener('resize', updateHeaderHeight);

        const resizeObserver = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(updateHeaderHeight)
            : null;
        resizeObserver?.observe(header);

        return () => {
            window.removeEventListener('resize', updateHeaderHeight);
            resizeObserver?.disconnect();
        };
    }, [currentUsecase]);

    if (!currentUsecase) {
        return (
            <div className="usecase-header-sticky-space" style={{ height: headerHeight }}>
                <div ref={headerRef} className="usecase-header-container loading-usecase-header" />
            </div>
        );
    }

    const { usecase_name } = currentUsecase;
    const taskDescription = (currentUsecase?.usecase_goal || '').trim();

    const handleBack = () => navigate('/');

    const subtasks = currentSubGroup?.usecases;
    const subtasksReady = Array.isArray(subtasks) && subtasks.length > 0;
    const showTaskPromptButton =
        typeof onOpenTaskPrompt === 'function'
        && subtasksReady
        && allSubtasksShareSameConfig(subtasks);

    const actionBarContent = (
        <div className="spark-header-action-bar">
            {showTaskPromptButton && (
                <Button
                    className="task-prompt-button"
                    variant="tertiary"
                    size="medium"
                    startIcon={<SparkIcon slug="edit" size="medium" />}
                    onClick={onOpenTaskPrompt}
                    disabled={role === 'Viewer'}
                    disableWhenReadOnly
                >
                    Task Prompt
                </Button>
            )}
        </div>
    );

    return (
        <div className="usecase-header-sticky-space" style={{ height: headerHeight }}>
            <div ref={headerRef} className="usecase-header-container">
                <Header
                    breadcrumb={<TaskBreadcrumb onCreateProject={onCreateProject} onAddTask={onAddTask} />}
                    showBackButton
                    onBackClick={handleBack}
                    title={(currentSubGroup?.sub_group_name || currentUsecase.sub_group_name || usecase_name || '').trim() || usecase_name}
                    subtitle={taskDescription || 'Follow the steps below to finetune and distill this model'}
                    actionBar={actionBarContent}
                    showActionBar={showTaskPromptButton}
                />
            </div>
        </div>
    );
};

export default UsecaseHeader;
